// membersService.js
// Service to preload and cache members data for fast agent contact modal access

// Custom cache for members with 24-hour TTL (different from simpleCache's 5-min default)
const MEMBERS_CACHE_KEY = 'rmkp_members';
const MEMBERS_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours (matches server cache)

/**
 * Get cached members from localStorage
 */
function getMembersCache() {
  try {
    const cached = localStorage.getItem(MEMBERS_CACHE_KEY);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache has expired
    if (now - timestamp > MEMBERS_CACHE_DURATION) {
      localStorage.removeItem(MEMBERS_CACHE_KEY);
      return null;
    }

    return data;
  } catch (error) {
    localStorage.removeItem(MEMBERS_CACHE_KEY);
    return null;
  }
}

/**
 * Set cached members in localStorage
 */
function setMembersCache(data) {
  try {
    const cacheData = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(MEMBERS_CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Failed to cache members data:', error);
  }
}

let membersCache = null;
let loadPromise = null;
let isLoading = false;

/**
 * Get cached members data if available
 */
function getCachedMembers() {
  // Check in-memory cache first (fastest)
  if (membersCache) {
    return membersCache;
  }

  // Check localStorage cache
  const cached = getMembersCache();
  if (cached) {
    membersCache = cached;
    return cached;
  }

  return null;
}

/**
 * Load members data from API
 * Uses cache if available and not expired
 */
async function loadMembersData() {
  // If already loading, return the existing promise
  if (isLoading && loadPromise) {
    return loadPromise;
  }

  // Check cache first
  const cached = getCachedMembers();
  if (cached) {
    return cached;
  }

  // Start loading
  isLoading = true;
  loadPromise = (async () => {
    try {
      const response = await fetch('/api/members');
      if (!response.ok) {
        throw new Error(`Failed to fetch members: ${response.status}`);
      }

      const data = await response.json();
      const members = data.value || data;

      // Cache the data
      membersCache = members;
      setMembersCache(members);

      console.log(`✅ Preloaded ${members.length} members`);
      return members;
    } catch (error) {
      console.error('❌ Failed to load members:', error);
      throw error;
    } finally {
      isLoading = false;
      loadPromise = null;
    }
  })();

  return loadPromise;
}

/**
 * Find a member by MemberKey
 * @param {string} memberKey - The MemberKey to search for
 * @returns {Promise<Object|null>} Member object or null if not found
 */
async function getMemberByKey(memberKey) {
  if (!memberKey) return null;

  // Load members if not already loaded
  const members = await loadMembersData();
  
  // Find the specific member
  const member = members.find(m => m.MemberKey === memberKey);
  
  if (!member) return null;

  // Transform to expected format
  return {
    name: `${member.MemberFirstName || ''} ${member.MemberLastName || ''}`.trim(),
    firstName: member.MemberFirstName,
    lastName: member.MemberLastName,
    fullName: member.MemberFullName,
    phone: member.MemberMobilePhone || member.MemberPreferredPhone || member.MemberOfficePhone || null,
    email: member.MemberEmail || null,
    memberKey: member.MemberKey,
    office: 'REMAX Key Properties'
  };
}

/**
 * Preload members data on app startup
 * Call this early in the app lifecycle
 */
export function preloadMembers() {
  // Start loading in background, don't await
  loadMembersData().catch(error => {
    console.warn('Failed to preload members (will retry on demand):', error);
  });
}

/**
 * Get member data by key (synchronous if cached, async if not)
 * @param {string} memberKey - The MemberKey to search for
 * @returns {Object|null} Member object or null if not found
 */
export function getMemberByKeySync(memberKey) {
  if (!memberKey) return null;

  const members = getCachedMembers();
  if (!members) return null;

  const member = members.find(m => m.MemberKey === memberKey);
  if (!member) return null;

  // Transform to expected format
  return {
    name: `${member.MemberFirstName || ''} ${member.MemberLastName || ''}`.trim(),
    firstName: member.MemberFirstName,
    lastName: member.MemberLastName,
    fullName: member.MemberFullName,
    phone: member.MemberMobilePhone || member.MemberPreferredPhone || member.MemberOfficePhone || null,
    email: member.MemberEmail || null,
    memberKey: member.MemberKey,
    office: 'REMAX Key Properties'
  };
}

/**
 * Get member data by key (async - will fetch if not cached)
 * @param {string} memberKey - The MemberKey to search for
 * @returns {Promise<Object|null>} Member object or null if not found
 */
export async function getMemberByKeyAsync(memberKey) {
  return getMemberByKey(memberKey);
}

export default {
  preloadMembers,
  getMemberByKey: getMemberByKeyAsync,
  getMemberByKeySync,
  loadMembersData
};

